import { AvatarConfig, VrmFilterConfig } from "../avatar/common/player/type";
import { ResourceType } from "../base/loader/type";
import { SmartScenarioBuilders } from "../avatar/common/player/util";
import { defaultConfig } from "./default.config";
import type { CameraConfig } from '../base/camera/type';
import type { LightingConfig } from '../base/light/type';
import type { RendererConfig } from '../base/renderer/type';
import type { LoaderConfig } from '../base/loader/type';
import type { ScenarioConfigs } from '../avatar/common/animation/type';

// Taekwondo 리소스 설정
// 모델·애니메이션 모두 전용 리소스 (2026-08-20 전용 VRMA 11종 수령, 원본명은 파일 헤더 주석 참고)
// - greeting_entrance02: 첫 인사 음성 시작에 맞춰 1회 재생
// - idle_standby01/02: 원본 waiting01_a/_b — 대기 중 랜덤 전환 루프
// - idle_standby03~06: 액션 모션 (2026-08-20 추가분: Right/Left arm punch, Right/Left leg side kick)
//   대기 셔플에 섞여 무음·무발화 시 랜덤 재생 (1~2초 단타, 사이클마다 순서 셔플)
// - input_listening01_a/_b, input_listening02_a/_b: 등록 리소스
//   (현재 듣기 시나리오에서는 미사용 — idle_standby01 팔·손 중심 자세 유지)
// - input_processing01: 원본 processing01_a
// - response_speaking01~03: 원본 speaking01~03 (현재 발화에는 팔·손 중심 02만 사용)
export const taekwondoResources: ResourceType[] = [
  { name: "taekwondo", type: "model_only", path: "/vrm/tkd-outline-outline-c93ce96a.vrm", format: "vrm" },
  { name: "greeting_entrance01", type: "animation_only", path: "/vrma/taekwondo@greeting_entrance01.vrma", format: "vrma" },
  { name: "greeting_entrance02", type: "animation_only", path: "/vrma/taekwondo@greeting_entrance02.vrma", format: "vrma" },
  { name: "idle_standby01", type: "animation_only", path: "/vrma/taekwondo@idle_standby01.vrma", format: "vrma" },
  { name: "idle_standby02", type: "animation_only", path: "/vrma/taekwondo@idle_standby02.vrma", format: "vrma" },
  { name: "idle_standby03", type: "animation_only", path: "/vrma/taekwondo@idle_standby03.vrma", format: "vrma" },
  { name: "idle_standby04", type: "animation_only", path: "/vrma/taekwondo@idle_standby04.vrma", format: "vrma" },
  { name: "idle_standby05", type: "animation_only", path: "/vrma/taekwondo@idle_standby05.vrma", format: "vrma" },
  { name: "idle_standby06", type: "animation_only", path: "/vrma/taekwondo@idle_standby06.vrma", format: "vrma" },
  { name: "input_listening01_a", type: "animation_only", path: "/vrma/taekwondo@input_listening01_a.vrma", format: "vrma" },
  { name: "input_listening01_b", type: "animation_only", path: "/vrma/taekwondo@input_listening01_b.vrma", format: "vrma" },
  { name: "input_listening02_a", type: "animation_only", path: "/vrma/taekwondo@input_listening02.vrma", format: "vrma" },
  { name: "input_listening02_b", type: "animation_only", path: "/vrma/taekwondo@input_listening02.vrma", format: "vrma" },
  { name: "input_processing01", type: "animation_only", path: "/vrma/taekwondo@input_processing01.vrma", format: "vrma" },
  { name: "response_speaking01", type: "animation_only", path: "/vrma/taekwondo@response_speaking01.vrma", format: "vrma" },
  { name: "response_speaking02", type: "animation_only", path: "/vrma/taekwondo@response_speaking02.vrma", format: "vrma" },
  { name: "response_speaking03", type: "animation_only", path: "/vrma/taekwondo@response_speaking03.vrma", format: "vrma" }
];

/**
 * Taekwondo 시나리오 설정 - 스마트 빌더 사용
 */
const TAEKWONDO_SCENARIOS: ScenarioConfigs<string> = {
  entrance: {
    animations: ['greeting_entrance02'],
    playMode: 'once',
    blendDuration: 0.2,
    description: "입장 애니메이션",
    nextScenario: 'speaking',
  },

  toss: {
    ...SmartScenarioBuilders.auto("idle_standby", taekwondoResources, {
      preferredMode: 'loop',
      description: "결제 대기 상태"
    }),
    // 대기 진입은 차분한 모션으로 시작하고 10초 후부터 기존 6초 랜덤 로테이션을 시작한다.
    // 발차기·펀치는 첫 10초 동안 나오지 않고 이후 셔플에 포함된다.
    options: {
      isRandom: true,
      maxClipSec: 6,
      entryMaxClipSec: 10,
      entryClips: ['idle_standby01', 'idle_standby02'],
    },
  },

  processing: SmartScenarioBuilders.auto("input_processing", taekwondoResources, {
    preferredMode: 'last_loop',
    description: "입력 처리 중 상태"
  }),

  // 수동 정의: 몸 회전·발차기 없이 팔·손 중심 자세만 반복한다.
  listening: {
    animations: ['idle_standby01'],
    playMode: 'last_loop',
    selectionMode: 'none',
    blendDuration: 0.2,
    animationPattern: 'single',
    description: "사용자 입력을 듣는 상태 (팔·손 중심 자세 유지)",
  },

  speaking: {
    animations: ['response_speaking02'],
    playMode: 'last_loop',
    selectionMode: 'none',
    blendDuration: 0.2,
    animationPattern: 'single',
    description: "말하기 상태 (팔·손 중심 모션)",
  },
};

// 카메라 설정 — 컨트롤 패널 튜닝값 고정 (2026-08-20)
// 망원 프레이밍: fov 10, position (0,1,5), rotation.x 0.25° (패널 export가 degree 단위)
const taekwondoCameraConfig: CameraConfig = {
  ...defaultConfig.camera,
  cameras: [
    {
      name: "taekwondo_main_camera",
      type: "PerspectiveCamera",
      active: true,
      fov: 10,
      position: { x: 0, y: 1, z: 5 },
      rotation: {
        x: 0.25 * Math.PI / 180,
        y: 0,
        z: 0
      },
      near: 0.1,
      far: 200
    }
  ]
};

// 조명 설정 - 최소한의 조명만 설정
const taekwondoLightConfig: LightingConfig = {
  ...defaultConfig.lighting,
};

// 렌더러 설정 — 컨트롤 패널 튜닝값 고정 (bloom 0.5/0/0.8, blend 0.1/1, exposure 1 = 기본값과 동일)
const taekwondoRendererConfig: RendererConfig = {
  ...defaultConfig.renderer,
};

// 로더 설정
const taekwondoLoaderConfig: LoaderConfig = {
  ...defaultConfig.loader,
  options: {
    ...defaultConfig.loader.options,
    isVRM: true,
  }
};

// 태권낭자 outline 모델 립싱크 — vrm_filter 이름 기반 풀 페이스 재생
// tkd-outline-outline 모델의 'Face' 메시(프리미티브 Face_1~_3)는 ARKit 표준명 모프 풀세트를
// 갖추고 있어 morphTargetDictionary 이름 매핑으로 서버 52채널(입+눈썹+표정+깜빡임)을
// 그대로 재생한다. 시선(eyeLook*)은 모프 대신 본/애니메이션이 담당하므로 제외.
// 깜빡임: 발화 중엔 서버(v18m) 깜빡임 채널, 대기 중엔 엔진 유휴 깜빡임이 구동.
const taekwondoLipsyncConfig: VrmFilterConfig = {
  excludeNames: [
    'eyeLookDownLeft', 'eyeLookDownRight',
    'eyeLookInLeft', 'eyeLookInRight',
    'eyeLookOutLeft', 'eyeLookOutRight',
    'eyeLookUpLeft', 'eyeLookUpRight',
  ],
  channelGains: {
    // 서버 깜빡임 피크(0.7)를 완전 감김 수준으로 증폭
    eyeBlinkLeft: 1.4,
    eyeBlinkRight: 1.4,
    // v18m jawOpen 진폭이 절제된 편이라 입 벌림 소폭 보강 (육안 QA 후 조정)
    jawOpen: 1.4,
  },
};

/**
 * Taekwondo 아바타 설정
 */
export const taekwondoAvatarConfig: AvatarConfig = {
  type: 'taekwondo' as const,
  modelKey: 'taekwondo',
  resources: taekwondoResources,
  scenarioConfigs: TAEKWONDO_SCENARIOS,
  springBoneEnabled: true,
  // VRM1.0 + 로더의 rotateVRM0로 이미 +Z 정면 → 추가 회전 불필요 (bobbi와 동일)
  rotation: {
    x: 0,
    y: 0,
    z: 0
  },
  lipsync: {
    processingType: 'vrm_filter',
    config: taekwondoLipsyncConfig,
  },
};

// Taekwondo 통합 설정 인터페이스
export interface TaekwondoConfig {
  loader: LoaderConfig;
  camera: CameraConfig;
  lighting: LightingConfig;
  renderer: RendererConfig;
  avatar: AvatarConfig;
}

/**
 * Taekwondo 설정 통합 객체
 */
export const taekwondoConfig: TaekwondoConfig = {
  loader: taekwondoLoaderConfig,
  camera: taekwondoCameraConfig,
  lighting: taekwondoLightConfig,
  renderer: taekwondoRendererConfig,
  avatar: taekwondoAvatarConfig
};
